import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Activity,
  AlertTriangle,
  CreditCard,
  Eye,
  LogIn,
  RefreshCw,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import { useToast } from '../../Common/context/ToastContext';
import { getAdminActivityLogs } from '../api/activity';

const PAGE_SIZE = 100;
const rowsOf = (data) => data?.rows ?? data?.list ?? (Array.isArray(data) ? data : []);

const activityLabels = {
  LOGIN: '로그인',
  LOGIN_FAILED: '로그인 실패',
  LOGOUT: '로그아웃',
  PAYMENT_APPROVED: '결제 승인',
  PAYMENT_FAILED: '결제 실패',
  PAYMENT_CANCELED: '결제 취소',
  PAYMENT_CANCEL_FAILED: '결제 취소 실패',
};

function initialFilters() {
  const today = dayjs().format('YYYY-MM-DD');
  return { dateFrom: today, dateTo: today, q: '', actorType: '', activityType: '', result: '' };
}

function normalizeActivity(row = {}, index = 0) {
  const activityType = String(row.activityType ?? row.activity_type ?? '').toUpperCase();
  const actorType = String(row.actorType ?? row.actor_type ?? '').toUpperCase();
  const result = String(row.result ?? '').toUpperCase();
  const actorId = row.actorId ?? row.actor_id ?? '-';
  const actorName = row.actorName ?? row.actor_name ?? '';
  const amountValue = row.amount;
  return {
    id: row.id ?? row.logId ?? row.log_id ?? `${activityType}-${index}`,
    occurredAt: row.occurredAt ?? row.occurred_at ?? '',
    actorType,
    actorId,
    actorName: actorName || actorId,
    cpId: row.cpId ?? row.cp_id ?? '-',
    cpName: row.cpName ?? row.cp_name ?? '-',
    activityType,
    activityLabel: row.activityLabel ?? row.activity_label ?? activityLabels[activityType] ?? activityType ?? '-',
    targetType: row.targetType ?? row.target_type ?? '-',
    targetId: row.targetId ?? row.target_id ?? '-',
    targetName: row.targetName ?? row.target_name ?? '-',
    paymentTransactionId: row.paymentTransactionId ?? row.payment_transaction_id ?? '-',
    amount: amountValue === null || amountValue === undefined || amountValue === '' ? null : Number(amountValue),
    result,
    ipAddress: row.ipAddress ?? row.ip_address ?? '-',
    device: row.device ?? row.userAgent ?? row.user_agent ?? '-',
    description: row.description ?? '-',
  };
}

function formatDateTime(value) {
  const parsed = dayjs(value);
  return value && parsed.isValid() ? parsed.format('YYYY.MM.DD HH:mm:ss') : '-';
}

function actorLabel(value) {
  if (value === 'CP') return '가맹점';
  if (value === 'TERMINAL') return '영업자';
  return value || '-';
}

function activityTone(row) {
  if (row.result === 'FAILURE' || row.activityType.includes('FAILED')) return 'bg-red-50 text-red-600';
  if (row.activityType.includes('CANCEL')) return 'bg-amber-50 text-amber-700';
  if (row.activityType === 'PAYMENT_APPROVED') return 'bg-emerald-50 text-emerald-700';
  if (row.activityType === 'LOGIN') return 'bg-blue-50 text-blue-700';
  return 'bg-slate-100 text-slate-600';
}

function Summary({ icon: Icon, label, value, accent = false }) {
  return <div className="flex items-center gap-3 rounded-xl border border-[#e5e8eb] bg-white px-4 py-3"><span className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent ? 'bg-red-50 text-red-600' : 'bg-[var(--brand-primary-soft)] text-[var(--brand-primary)]'}`}><Icon size={18} /></span><div><p className="text-[11px] text-[#8b95a1]">{label}</p><strong className="mt-0.5 block text-[19px] font-extrabold text-[#191f28]">{Number(value || 0).toLocaleString('ko-KR')}건</strong></div></div>;
}

function DetailItem({ label, value, mono = false }) {
  return <div className="border-b border-[#f0f2f1] py-3 last:border-0"><span className="block text-[11px] text-[#8b95a1]">{label}</span><strong className={`mt-1 block break-words text-[13px] font-semibold text-[#191f28] ${mono ? 'font-mono' : ''}`}>{value === null || value === undefined || value === '' ? '-' : value}</strong></div>;
}

function ActivityDetailModal({ row, onClose }) {
  if (!row) return null;
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="flex max-h-[86vh] w-full max-w-[640px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="flex shrink-0 items-center justify-between border-b border-[#eef1f4] px-6 py-4"><div><h2 className="text-[19px] font-extrabold text-[#191f28]">활동 상세</h2><p className="mt-1 text-[11px] text-[#8b95a1]">로그 #{row.id}</p></div><button type="button" onClick={onClose} className="rounded-lg p-2 text-[#8b95a1] hover:bg-[#f2f4f6]" aria-label="닫기"><X size={20} /></button></header>
        <div className="min-h-0 overflow-y-auto px-6 py-2">
          <div className="grid grid-cols-2 gap-x-6"><DetailItem label="발생 일시" value={formatDateTime(row.occurredAt)} /><DetailItem label="사용자 유형" value={actorLabel(row.actorType)} /><DetailItem label="로그인 ID" value={row.actorId} mono /><DetailItem label="사용자 이름" value={row.actorName} /><DetailItem label="가맹점 ID" value={row.cpId} mono /><DetailItem label="가맹점명" value={row.cpName} /><DetailItem label="활동 유형" value={`${row.activityLabel} (${row.activityType})`} /><DetailItem label="성공 여부" value={row.result === 'SUCCESS' ? '성공' : '실패'} /><DetailItem label="거래처 코드" value={row.targetId} mono /><DetailItem label="거래처명" value={row.targetName} /><DetailItem label="결제 트랜잭션 PK" value={row.paymentTransactionId} mono /><DetailItem label="금액" value={row.amount === null ? '-' : `${row.amount.toLocaleString('ko-KR')}원`} /><DetailItem label="IP 주소" value={row.ipAddress} mono /><DetailItem label="User-Agent" value={row.device} /></div>
          <DetailItem label="설명" value={row.description} />
        </div>
        <footer className="shrink-0 border-t border-[#eef1f4] bg-[#f9fafb] px-6 py-4"><button type="button" onClick={onClose} className="w-full rounded-lg bg-[#191f28] py-3 text-[13px] font-bold text-white">닫기</button></footer>
      </div>
    </div>
  );
}

export default function StatusLog() {
  const { showToast } = useToast();
  const [draft, setDraft] = useState(initialFilters);
  const [applied, setApplied] = useState(initialFilters);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ loginCount: 0, paymentCount: 0, failureCount: 0 });
  const [loading, setLoading] = useState(true);
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [checkedAt, setCheckedAt] = useState(null);
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAdminActivityLogs({
        page,
        limit: PAGE_SIZE,
        dateFrom: `${applied.dateFrom}T00:00:00+09:00`,
        dateTo: `${applied.dateTo}T23:59:59.999+09:00`,
        q: applied.q,
        actorType: applied.actorType,
        activityType: applied.activityType,
        result: applied.result,
      });
      const responseRows = rowsOf(data);
      setRows(responseRows.map(normalizeActivity));
      setTotal(Number(data?.total ?? responseRows.length));
      setSummary({
        loginCount: Number(data?.summary?.loginCount ?? 0),
        paymentCount: Number(data?.summary?.paymentCount ?? 0),
        failureCount: Number(data?.summary?.failureCount ?? 0),
      });
      setApiUnavailable(false);
      setCheckedAt(new Date());
    } catch (error) {
      setRows([]);
      setTotal(0);
      setSummary({ loginCount: 0, paymentCount: 0, failureCount: 0 });
      setApiUnavailable(error?.response?.status === 404);
      setCheckedAt(new Date());
      showToast(error?.message || '활동 이력을 불러오지 못했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [applied, page, refreshKey, showToast]);

  useEffect(() => { load(); }, [load]);

  const updateDraft = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const submitFilters = (event) => {
    event.preventDefault();
    if (!draft.dateFrom || !draft.dateTo || draft.dateFrom > draft.dateTo) {
      showToast('조회 기간을 확인해 주세요.', 'error');
      return;
    }
    setPage(1);
    setApplied({ ...draft, q: draft.q.trim() });
    setRefreshKey((value) => value + 1);
  };
  const resetFilters = () => {
    const reset = initialFilters();
    setDraft(reset);
    setApplied(reset);
    setPage(1);
    setRefreshKey((value) => value + 1);
  };
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-[#f6f9f7] px-5 py-5 lg:px-7">
      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-4">
        <header className="flex items-end justify-between gap-4"><div><h1 className="text-[23px] font-extrabold text-[#191f28]">활동 현황</h1><p className="mt-1 text-[12px] text-[#6b7684]">가맹점과 영업자의 로그인·결제 활동을 시간순으로 감시합니다.</p></div><div className="flex items-center gap-2 text-[11px] text-[#8b95a1]">{checkedAt && <span>{dayjs(checkedAt).format('HH:mm:ss')} 갱신</span>}<button type="button" onClick={() => setRefreshKey((value) => value + 1)} disabled={loading} className="flex h-9 items-center gap-1.5 rounded-lg border border-[#dfe3e8] bg-white px-3 font-semibold text-[#4e5968] hover:bg-[#f9fafb] disabled:opacity-50"><RefreshCw size={14} className={loading ? 'animate-spin' : ''} />새로고침</button></div></header>

        <section className="grid grid-cols-1 gap-3 sm:grid-cols-3"><Summary icon={LogIn} label="로그인 활동" value={summary.loginCount} /><Summary icon={CreditCard} label="결제 활동" value={summary.paymentCount} /><Summary icon={AlertTriangle} label="실패 활동" value={summary.failureCount} accent={summary.failureCount > 0} /></section>

        {apiUnavailable && <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-800"><strong>활동 이력 API를 찾을 수 없습니다.</strong><span className="ml-2">`GET /api/admin/activity-logs` 배포 상태를 확인해 주세요.</span></div>}

        <form onSubmit={submitFilters} className="flex flex-wrap items-end gap-2 rounded-xl border border-[#dfe5e1] bg-white p-4">
          <label className="text-[11px] font-semibold text-[#6b7684]">시작일<input type="date" value={draft.dateFrom} onChange={(event) => updateDraft('dateFrom', event.target.value)} className="mt-1 block h-9 rounded-lg border border-[#dfe3e8] px-2 text-[12px]" /></label>
          <label className="text-[11px] font-semibold text-[#6b7684]">종료일<input type="date" value={draft.dateTo} onChange={(event) => updateDraft('dateTo', event.target.value)} className="mt-1 block h-9 rounded-lg border border-[#dfe3e8] px-2 text-[12px]" /></label>
          <label className="text-[11px] font-semibold text-[#6b7684]">사용자 유형<select value={draft.actorType} onChange={(event) => updateDraft('actorType', event.target.value)} className="mt-1 block h-9 rounded-lg border border-[#dfe3e8] bg-white px-2 text-[12px]"><option value="">전체</option><option value="CP">가맹점</option><option value="TERMINAL">영업자</option></select></label>
          <label className="text-[11px] font-semibold text-[#6b7684]">활동 유형<select value={draft.activityType} onChange={(event) => updateDraft('activityType', event.target.value)} className="mt-1 block h-9 rounded-lg border border-[#dfe3e8] bg-white px-2 text-[12px]"><option value="">전체</option><option value="LOGIN">로그인</option><option value="LOGOUT">로그아웃</option><option value="PAYMENT">결제</option><option value="CANCEL">취소</option></select></label>
          <label className="text-[11px] font-semibold text-[#6b7684]">성공 여부<select value={draft.result} onChange={(event) => updateDraft('result', event.target.value)} className="mt-1 block h-9 rounded-lg border border-[#dfe3e8] bg-white px-2 text-[12px]"><option value="">전체</option><option value="SUCCESS">성공</option><option value="FAILURE">실패</option></select></label>
          <label className="min-w-[220px] flex-1 text-[11px] font-semibold text-[#6b7684]">검색어<span className="mt-1 flex h-9 items-center gap-2 rounded-lg border border-[#dfe3e8] px-3 focus-within:border-[var(--brand-primary)]"><Search size={15} className="text-[#8b95a1]" /><input value={draft.q} onChange={(event) => updateDraft('q', event.target.value)} placeholder="이름, ID, 가맹점, 거래처 검색" className="min-w-0 flex-1 border-0 bg-transparent text-[12px] outline-none" /></span></label>
          <button type="submit" className="flex h-9 items-center gap-1.5 rounded-lg bg-[var(--brand-primary)] px-4 text-[12px] font-bold text-white hover:bg-[var(--brand-primary-hover)]"><Search size={14} />조회</button>
          <button type="button" onClick={resetFilters} className="flex h-9 items-center gap-1.5 rounded-lg border border-[#dfe3e8] bg-white px-3 text-[12px] font-semibold text-[#4e5968] hover:bg-[#f9fafb]"><RotateCcw size={14} />초기화</button>
        </form>

        <section className="overflow-hidden rounded-xl border border-[#dfe5e1] bg-white">
          <div className="flex h-[48px] items-center gap-2 border-b border-[#eef1f4] px-4"><Activity size={17} className="text-[var(--brand-primary)]" /><h2 className="text-[15px] font-bold text-[#191f28]">활동 이력</h2><span className="text-[11px] text-[#8b95a1]">총 {total.toLocaleString('ko-KR')}건</span></div>
          <div className="max-h-[calc(100dvh-390px)] min-h-[340px] overflow-auto">
            <table className="w-full table-fixed text-left text-[11px]">
              <thead className="sticky top-0 z-10 bg-[#f9fafb] text-[#6b7684]"><tr className="h-10 border-b border-[#e5e8eb]"><th className="w-[12%] px-2">발생 일시</th><th className="w-[7%] px-2">유형</th><th className="w-[10%] px-2">사용자</th><th className="w-[9%] px-2">로그인 ID</th><th className="w-[10%] px-2">가맹점</th><th className="w-[9%] px-2">활동</th><th className="w-[10%] px-2">거래처</th><th className="w-[7%] px-2 text-right">금액</th><th className="w-[6%] px-2 text-center">결과</th><th className="w-[8%] px-2">IP</th><th className="w-[7%] px-2">상세</th><th className="w-[5%] px-2 text-center">관리</th></tr></thead>
              <tbody className="divide-y divide-[#f0f2f1]">
                {loading ? <tr><td colSpan={12} className="py-20 text-center text-[#8b95a1]">활동 이력을 불러오는 중입니다.</td></tr> : rows.length === 0 ? <tr><td colSpan={12} className="py-20 text-center text-[#8b95a1]">조회된 활동 이력이 없습니다.</td></tr> : rows.map((row) => <tr key={row.id} onClick={() => setSelected(row)} className="h-[52px] cursor-pointer hover:bg-[#f9fafb]"><td className="whitespace-nowrap px-2 tabular-nums text-[#4e5968]">{formatDateTime(row.occurredAt)}</td><td className="px-2"><span className={`rounded px-1.5 py-1 font-bold ${row.actorType === 'CP' ? 'bg-blue-50 text-blue-700' : 'bg-[var(--brand-primary-soft)] text-[var(--brand-primary)]'}`}>{actorLabel(row.actorType)}</span></td><td className="truncate px-2 font-bold text-[#191f28]">{row.actorName}</td><td className="truncate px-2 font-mono text-[#4e5968]">{row.actorId}</td><td className="truncate px-2" title={row.cpName}>{row.cpName}</td><td className="px-2"><span className={`inline-block max-w-full truncate rounded px-1.5 py-1 font-bold ${activityTone(row)}`}>{row.activityLabel}</span></td><td className="truncate px-2" title={row.targetName}>{row.targetName}</td><td className="whitespace-nowrap px-2 text-right font-semibold tabular-nums">{row.amount === null ? '-' : `${row.amount.toLocaleString('ko-KR')}원`}</td><td className="px-2 text-center"><span className={`rounded px-1.5 py-1 font-bold ${row.result === 'SUCCESS' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{row.result === 'SUCCESS' ? '성공' : '실패'}</span></td><td className="truncate px-2 font-mono text-[#8b95a1]">{row.ipAddress}</td><td className="truncate px-2" title={row.description}>{row.description}</td><td className="px-2 text-center"><button type="button" onClick={(event) => { event.stopPropagation(); setSelected(row); }} className="rounded border border-[#dfe3e8] p-1.5 text-[#4e5968] hover:bg-white" aria-label="상세 보기"><Eye size={14} /></button></td></tr>)}
              </tbody>
            </table>
          </div>
          {total > PAGE_SIZE && <div className="flex items-center justify-center gap-3 border-t border-[#eef1f4] px-4 py-2.5 text-[12px]"><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border border-[#e5e8eb] px-3 py-1.5 disabled:opacity-40">이전</button><span>{page} / {lastPage}</span><button type="button" disabled={page >= lastPage} onClick={() => setPage((value) => value + 1)} className="rounded border border-[#e5e8eb] px-3 py-1.5 disabled:opacity-40">다음</button></div>}
        </section>
      </div>
      <ActivityDetailModal row={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
